/**
 * Pure utility functions with zero side effects.
 * These functions contain algorithmic logic extracted from legacy code.
 *
 * IMPORTANT: This module MUST NOT import any external SDKs or have side effects.
 * All functions must be pure: input → output, deterministic, no mutations.
 */
/**
 * Computes the KMP (Knuth-Morris-Pratt) failure function (LPS array).
 * This is used for efficient pattern matching in text search.
 *
 * @param pattern The pattern string to compute the failure function for
 * @returns Longest proper prefix array (LPS array)
 *
 * @example
 * ```typescript
 * const lps = computeKMPTable("abcabc");
 * // Returns: [0, 0, 0, 1, 2, 3]
 * ```
 */
export const computeKMPTable = (pattern) => {
    if (!pattern || pattern.length === 0) {
        return [];
    }
    const lps = new Array(pattern.length).fill(0);
    let len = 0;
    let i = 1;
    while (i < pattern.length) {
        if (pattern[i] === pattern[len]) {
            len++;
            lps[i] = len;
            i++;
        }
        else {
            if (len !== 0) {
                len = lps[len - 1];
            }
            else {
                lps[i] = 0;
                i++;
            }
        }
    }
    return lps;
};
/**
 * Finds all occurrences of a pattern in text using the KMP algorithm.
 *
 * @param text The text to search in
 * @param pattern The pattern to search for
 * @param startPos Starting position offset (default: 0)
 * @param maxOccurrences Maximum number of occurrences to find (optional)
 * @returns Array of start positions (0-based indices in the text)
 *
 * @example
 * ```typescript
 * const positions = kmpSearch("abcabcabc", "abc", 0, 2);
 * // Returns: [0, 3] (first 2 occurrences)
 * ```
 */
export const kmpSearch = (text, pattern, startPos = 0, maxOccurrences) => {
    const positions = [];
    if (!pattern || !text || pattern.length > text.length) {
        return positions;
    }
    const lps = computeKMPTable(pattern);
    let i = 0;
    let j = 0;
    while (i < text.length) {
        if (pattern[j] === text[i]) {
            i++;
            j++;
        }
        if (j === pattern.length) {
            positions.push(startPos + i - j);
            j = lps[j - 1];
            if (maxOccurrences && positions.length >= maxOccurrences) {
                break;
            }
        }
        else if (i < text.length && pattern[j] !== text[i]) {
            if (j !== 0) {
                j = lps[j - 1];
            }
            else {
                i++;
            }
        }
    }
    return positions;
};
/**
 * Maps text positions in a combined string back to document positions.
 * Used when searching across multiple text nodes that have been concatenated.
 *
 * @param textNodes Array of text nodes with their document positions
 * @param textPositions Positions in the combined text (from kmpSearch)
 * @param patternLength Length of the pattern being searched for
 * @returns Array of { from, to } ranges in document coordinates
 *
 * @example
 * ```typescript
 * const textNodes = [
 *   { text: "Hello ", pos: 0 },
 *   { text: "World", pos: 6 }
 * ];
 * const positions = [0]; // Found at start of combined text
 * const ranges = mapTextPositionsToDocument(textNodes, positions, 5);
 * // Returns: [{ from: 0, to: 5 }]
 * ```
 */
export const mapTextPositionsToDocument = (textNodes, textPositions, patternLength) => {
    const ranges = [];
    for (const textPos of textPositions) {
        let currentPos = 0;
        let startNodeIndex = -1;
        let startOffset = 0;
        let endNodeIndex = -1;
        let endOffset = 0;
        // Find the start and end nodes for this match
        for (let i = 0; i < textNodes.length; i++) {
            const node = textNodes[i];
            const nodeLength = node.text.length;
            // Check if this node contains the start of the match
            if (startNodeIndex === -1 && textPos < currentPos + nodeLength) {
                startNodeIndex = i;
                startOffset = textPos - currentPos;
            }
            // Check if this node contains the end of the match
            if (endNodeIndex === -1 && textPos + patternLength <= currentPos + nodeLength) {
                endNodeIndex = i;
                endOffset = textPos + patternLength - currentPos;
                break;
            }
            currentPos += nodeLength;
        }
        // If we found both start and end nodes, create the range
        if (startNodeIndex !== -1 && endNodeIndex !== -1) {
            const startPos = textNodes[startNodeIndex].pos + startOffset;
            const endPos = textNodes[endNodeIndex].pos + endOffset;
            ranges.push({ from: startPos, to: endPos });
        }
    }
    return ranges;
};
/**
 * Compares two maps for equality.
 * Checks if both maps have the same size and identical key-value pairs.
 *
 * @param map1 First map to compare
 * @param map2 Second map to compare
 * @returns True if maps are equal, false otherwise
 *
 * @example
 * ```typescript
 * const map1 = new Map([['a', true], ['b', false]]);
 * const map2 = new Map([['a', true], ['b', false]]);
 * areMapsEqual(map1, map2); // Returns: true
 * ```
 */
export const areMapsEqual = (map1, map2) => {
    if (map1.size !== map2.size)
        return false;
    for (const [key, value] of Array.from(map1)) {
        if (map2.get(key) !== value)
            return false;
    }
    return true;
};
